/* eslint-disable max-lines-per-function */
import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AxiosError } from "axios";
import Button from "@aleaplay/web-components/ui/Button";
import Modal from "@aleaplay/web-components/ui/Modal";
import Select from "@aleaplay/web-components/ui/Select";

import restclientNoTimeout from "@/v1/services/restclientNoTimeout";
import { ErrorResponse } from "@/v1/types/global";
import generatePeriods from "@/v1/utils/generatePeriods";

function downloadFile(response: any) {
  const url = window.URL.createObjectURL(new Blob([response]));
  const el = createDownloadLink(url);
  el.click();
}

function createDownloadLink(url: string) {
  const el = document.createElement("a");
  el.setAttribute("download", "currency-report.csv");
  el.setAttribute("href", url);
  document.body.appendChild(el);
  return el;
}

interface CurrencyReportModalProps {
  visible?: boolean;
  onClose?: (...arr: any[]) => void;
}

const CurrencyReportModal: React.FC<CurrencyReportModalProps> = ({
  visible = false,
  onClose = () => undefined,
}) => {
  const periods = useMemo(() => generatePeriods(), []);
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [from, setFrom] = useState(periods[0]);
  const [to, setTo] = useState("");

  useEffect(() => {
    return () => {
      setLoading(false);
    };
  }, [visible]);

  const handlerSubmit = async () => {
    setLoading(true);
    try {
      const response = await restclientNoTimeout.get(
        `/api/v1/currencies-report?from=${from}&to=${to}`,
        {
          headers: {
            Accept: "text/csv",
          },
          responseType: "blob",
          timeout: 0, // 0 = Cancel timeout
        },
      );
      downloadFile(response.data);
      onClose();
    } catch (err) {
      const errorResponse = (error &&
        typeof error === "object" &&
        (error as AxiosError<ErrorResponse>)?.response?.data) as ErrorResponse;
      setError(
        errorResponse.errors?.map(({ message }: any) => message).join(", ") || "Something happened",
      );
    } finally {
      setLoading(false);
    }
  };

  const periodsOptions = useMemo(() => {
    return periods.map(period => (
      <Select.Option key={period} value={period}>
        {period}
      </Select.Option>
    ));
  }, [periods]);

  return (
    <Modal
      visible={visible}
      title={intl.formatMessage({ id: "label.download-currency-report" })}
      actions={[
        <Button key="cancel" modifier="secondary" onClick={onClose}>
          <FormattedMessage id="label.cancel" />
        </Button>,
        <Button key="confirm" modifier="primary" type="submit" onClick={handlerSubmit}>
          <FormattedMessage id="label.download" />
        </Button>,
      ]}
      footerMessage={
        <FormattedMessage
          id="label.marked-fields-mandatory"
          values={{ marked: <span className="highlight">*</span> }}
        />
      }
      loading={loading}
      domNode={document.querySelector("#root")}
      onClose={onClose}
    >
      <Select
        required
        label={<FormattedMessage id="label.from" />}
        placeholder={<FormattedMessage id="select.placeholder" />}
        value={from}
        onChange={(value: any) => setFrom(value)}
      >
        {periodsOptions}
      </Select>
      <br />
      <Select
        label={<FormattedMessage id="label.to" />}
        placeholder={<FormattedMessage id="select.placeholder" />}
        value={to}
        onChange={(value: string) => setTo(value)}
      >
        {periodsOptions}
      </Select>
      <p style={{ color: "red" }}>{error !== "" && error}</p>
    </Modal>
  );
};

export default CurrencyReportModal;
